/* eslint-disable */
/* eslint-disable eslint-comments/no-unused-disable */
/* 
* The file is automatically generated by Serenity UI.
* 
* ⚠️ WARNING: Any manual changes may be overwritten during the next upgrade
* 
* Found an issue? Submit a task to the ClickUp board:
* https://app.clickup.com/1255586/v/l/6-901510012553-1?pr=90154030902
*/
import * as stylex from '@stylexjs/stylex';
import { type ForwardedRef, type ReactNode, createContext, forwardRef, useRef } from 'react';
import { useFocusRing, useHover, usePress } from 'react-aria';
import {
  type ContextValue,
  InputContext as InputContextCore,
  Input as InputCore,
  type InputProps as InputCoreProps,
  Provider,
  useContextProps,
  useRenderProps
} from 'react-aria-components';
import { $ } from '../../tokens.stylex';
import { IconButtonContext } from '../icon-button/icon-button.component';
import { IconContext } from '../icon/icon.component';
import { ProgressIndicatorContext } from '../progress-indicator/progress-indicator.component';

interface InputStyleProps {
  /** The size variant of the input. (Default: 48) */
  size?: keyof typeof sizeVariants;
  /** The variant of the input. (Default: outlined) */
  variant?: keyof typeof variantVariants;
  /** Used to add accessories to the input. */
  children?: ((values: {
    isHovered: boolean;
    isFocused: boolean;
    isFocusVisible: boolean;
    isDisabled: boolean;
    isInvalid: boolean;
    defaultChildren: ReactNode | undefined;
  }) => ReactNode);
  style?: stylex.StyleXStyles;
}

type InputProps = InputStyleProps & Omit<InputCoreProps, 'style' | 'className' | 'children'>;

const InputStyleContext = createContext<ContextValue<InputStyleProps, HTMLDivElement>>({});

const Input = forwardRef((props: InputProps, ref: ForwardedRef<HTMLDivElement>) => {
  let inputRef = useRef<HTMLInputElement>(null);
  let contextProps: InputCoreProps = {};

  [props, ref] = useContextProps(props, ref, InputStyleContext);
  [contextProps, inputRef] = useContextProps({}, inputRef, InputContextCore);

  const { style, size = 48, variant = 'outlined', children, ...coreProps } = props;

  const { hoverProps, isHovered } = useHover(coreProps);
  const { isFocused, isFocusVisible, focusProps } = useFocusRing({
    isTextInput: true,
    autoFocus: coreProps.autoFocus,
    within: true,
  });
  // onKeyDown prevents spaces from being entered into the input field
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { pressProps: { onKeyDown, ...pressProps } } = usePress({
    onPressStart: () => inputRef.current?.focus(),
    preventFocusOnPress: true,
  });

  const isInvalid = (!!coreProps['aria-invalid'] && coreProps['aria-invalid'] !== 'false') || !!contextProps['aria-invalid'];
  const isDisabled = coreProps.disabled || contextProps.disabled || false;

  const renderProps = useRenderProps({
    ...coreProps,
    values: {
      isHovered,
      isFocused,
      isFocusVisible,
      isDisabled,
      isInvalid
    },
    defaultChildren: (
      <InputCore
        ref={inputRef}
        {...coreProps}
        {...stylex.props(
          inputStyles.base,
          inputSizeVariants[size],
          isHovered && inputStyles.hovered,
          isFocusVisible && inputStyles.focused,
          isDisabled && inputStyles.disabled,
        )}
      />),
    children: (renderProps) => (
      <Provider values={[
        [ProgressIndicatorContext, {
          size: size === 48 ? 20 : 16,
          variant: 'primary',
        }],
        [IconButtonContext, {
          variant: 'ghost',
          size: size === 48 ? 32 : 20,
          isDisabled,
        }],
        [IconContext, {
          color: (isDisabled && 'disabled') || 'tertiary'
        }]]}>
        {children?.(renderProps) || renderProps.defaultChildren}
      </Provider>
    )
  });

  return (
    <div
      ref={ref}
      data-focused={isFocused || undefined}
      data-disabled={isDisabled || undefined}
      data-hovered={isHovered || undefined}
      data-focus-visible={isFocusVisible || undefined}
      data-invalid={isInvalid || undefined}
      {...pressProps}
      {...hoverProps}
      {...focusProps}
      {...renderProps}
      {...stylex.props(styles.base, sizeVariants[size], variantVariants[variant], style)}>
      {renderProps.children}
    </div>
  );
});

const styles = stylex.create({
  base: {
    alignItems: 'center',
    borderStyle: 'solid',
    boxSizing: 'border-box',
    columnGap: 12,
    cursor: {
      default: 'text',
      ':is([data-disabled])': 'not-allowed',
    },
    display: 'flex',
    overflow: 'hidden',
  },
});

const inputStyles = stylex.create({
  base: {
    alignItems: 'center',
    backgroundColor: 'transparent',
    borderStyle: 'none',
    boxSizing: 'border-box',
    caretColor: $.textInteractive,
    color: {
      default: $.textPrimary,
      ':is([data-disabled])': $.textDisabled,
    },
    cursor: 'inherit',
    display: 'flex',
    flex: '1 1 0',
    fontFamily: 'BasierCircle',
    fontFeatureSettings: 'liga off, clig off',
    fontStyle: 'normal',
    fontWeight: 400,
    height: '100%',
    minWidth: 0,
    outline: 'none',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    width: '100%',
    '::placeholder': { color: $.textPlaceholder },
    '::selection': { backgroundColor: $.surfaceHighlightMedium },
  },
  hovered: { '::placeholder': { color: $.textQuaternary } },
  disabled: { '::placeholder': { color: $.textDisabled } },
  focused: { '::placeholder': { color: $.textPlaceholder } },
});

const variantVariants = stylex.create({
  outlined: {
    backgroundColor: $.surfaceBGPrimary,
    borderColor: {
      default: $.border16,
      ':is([data-hovered]):not([data-focused], [data-focus-within], [data-invalid], [data-disabled])': $.border24,
      ':is([data-focused], [data-focus-within]):not([data-invalid])': $.borderInteractive,
      ':is([data-invalid])': $.borderCritical,
      ':is([data-disabled])': $.border10,
    },
    borderWidth: {
      default: 1,
      ':is([data-focused], [data-focus-within], [data-invalid])': 2,
      ':is([data-disabled])': 1,
    },
  },
  filled: {
    backgroundColor: {
      default: $.opacity06,
      ':is([data-focused], [data-focus-within]):not([data-invalid])': $.surfaceBGPrimary,
      ':is([data-invalid])': $.surfaceCriticalSilent,
    },
    borderColor: {
      default: 'transparent',
      ':is([data-focused], [data-focus-within]):not([data-invalid])': $.borderInteractive,
    },
    borderWidth: {
      default: 1,
      ':is([data-focused], [data-focus-within], [data-invalid])': 2,
      ':is([data-disabled])': 1,
    },
  },
  underlined: {
    backgroundColor: 'transparent',
    borderBottomColor: {
      default: $.border08,
      ':is([data-hovered], [data-focused], [data-focus-within]):not([data-invalid], [data-disabled])': $.borderInteractive,
      ':is([data-disabled])': $.border06,
      ':is([data-invalid])': $.borderCritical,
    },
    borderBottomWidth: 1.5,
    borderLeftWidth: 0,
    borderRadius: 0,
    borderRightWidth: 0,
    borderTopWidth: 0,
    paddingLeft: 0,
    paddingRight: 0,
  },
});

const sizeVariants = stylex.create({
  48: {
    borderRadius: 10,
    height: 48,
    paddingBottom: {
      default: 5,
      ':is([data-focused], [data-focus-within], [data-invalid], [aria-expanded="true"])': 4,
    },
    paddingLeft: {
      default: 14,
      ':is([data-focused], [data-focus-within], [data-invalid], [aria-expanded="true"])': 13,
    },
    paddingRight: {
      default: 14,
      ':is([data-focused], [data-focus-within], [data-invalid], [aria-expanded="true"])': 13,
    },
    paddingTop: {
      default: 5,
      ':is([data-focused], [data-focus-within], [data-invalid], [aria-expanded="true"])': 4,
    },
  },
  32: {
    borderRadius: 8,
    height: 32,
    paddingLeft: {
      default: 12,
      ':is([data-focused], [data-focus-within], [data-invalid], [aria-expanded="true"])': 11,
    },
    paddingRight: {
      default: 12,
      ':is([data-focused], [data-focus-within], [data-invalid], [aria-expanded="true"])': 11,
    },
  },
});

const inputSizeVariants = stylex.create({
  48: {
    fontSize: 16,
    letterSpacing: -0.32,
  },
  32: {
    fontSize: 14,
    letterSpacing: -0.252,
  },
});

export { Input, InputStyleContext, type InputProps, type InputStyleProps };
